/**
 * Turns the live KPI dashboard payload (sourced from Snowflake `revenue_waterfall`
 * + HubSpot) into a small set of chart-ready {@link ChartDataset}s. The generic
 * charting IR + AG Charts options builder now live in the DS component library
 * (`@/components/cinch/charts`); this file is the KPI-specific dataset factory
 * that feeds them, re-exporting the generic pieces so the lab's existing
 * imports resolve from one place.
 */
import {
  buildAgOptions,
  CHART_TYPE_LABEL,
  resolveToken,
  type ChartDataset,
  type ChartType,
  type CinchColor,
  type DatasetSeries,
} from "@/components/cinch/charts";
import { monthLabelShort, type KpiDashboardResponse } from "@/features/kpi/api";
import { fmtArr } from "@/features/kpi/format";

// Re-export the graduated charting IR for the lab's consumers (ChartsLabPage,
// RechartsPanel, the lab AgChartPanel adapter).
export { buildAgOptions, CHART_TYPE_LABEL };
export type { ChartType, ChartDataset, DatasetSeries };

/** Charts Lab's dataset ids. */
export type DatasetKey = "products" | "arr" | "retention" | "funnel";

const pct1 = (n: number): string => `${n.toFixed(1)}%`;
const count0 = (n: number): string => Math.round(n).toLocaleString("en-US");

/** Build every dataset that has data in this payload, resolving colors for the
 *  current mode (pass `isDark` so a theme flip re-resolves the palette). */
export function buildDatasets(
  resp: KpiDashboardResponse,
  // isDark only affects the resolved color values; kept in the signature so
  // callers memoize on it.
  _isDark: boolean,
): ChartDataset[] {
  const out: ChartDataset[] = [];
  const color = (c: CinchColor) => resolveToken(c);

  // ── Per-product ARR — the richest multi-series time series (mirrors the
  //    kpi ProductArrTrends component, so it's an apples-to-apples compare). ──
  const products = resp.products?.items ?? [];
  if (products.length > 0 && (resp.products?.months?.length ?? 0) > 0) {
    const months = resp.products.months;
    const rows = months.map((m, i) => {
      const row: Record<string, string | number | null> = { month: monthLabelShort(m) };
      for (const p of products) row[p.key] = p.arr[i] ?? null;
      return row;
    });
    out.push({
      key: "products",
      label: "Product ARR",
      description: `Annualized ARR by product line over ${months.length} months — ${products.length} series.`,
      kind: "timeseries",
      chartTypes: ["line", "area", "stacked-area", "bar", "stacked-bar", "horizontal-bar"],
      categoryKey: "month",
      rows,
      series: products.map((p): DatasetSeries => ({ key: p.key, name: p.name })),
      colors: products.map((p) => color((p.color as CinchColor) ?? "brand")),
      valueFormat: (n) => fmtArr(n, 1),
    });
  }

  // ── Total ARR trend — the canonical single time series. ──
  const trend = resp.arr?.trend ?? [];
  if (trend.length > 0) {
    out.push({
      key: "arr",
      label: "Total ARR trend",
      description: `Company-wide ARR across the trailing ${trend.length} months.`,
      kind: "timeseries",
      chartTypes: ["area", "line", "bar", "horizontal-bar"],
      categoryKey: "month",
      rows: trend.map((t) => ({ month: monthLabelShort(t.month), arr: t.arr })),
      series: [{ key: "arr", name: "Total ARR" }],
      colors: [color("brand")],
      valueFormat: (n) => fmtArr(n, 1),
    });
  }

  // ── Retention — two-line NRR / GDR trend. ──
  const retention = resp.retention?.series ?? [];
  if (resp.retention?.available && retention.length > 0) {
    out.push({
      key: "retention",
      label: "Retention (NRR / GDR)",
      description: "Trailing-12-month net & gross dollar retention.",
      kind: "timeseries",
      chartTypes: ["line", "area", "stacked-area", "bar", "horizontal-bar"],
      categoryKey: "month",
      rows: retention.map((r) => ({
        month: monthLabelShort(r.month),
        nrr: r.nrr,
        gdr: r.gdr,
      })),
      series: [
        { key: "nrr", name: "NRR %" },
        { key: "gdr", name: "GDR %" },
      ],
      colors: [color("brand"), color("teal")],
      valueFormat: pct1,
    });
  }

  // ── SQO outcomes — a part-to-whole categorical (great for bar/pie/donut). ──
  const f = resp.funnel;
  if (f && f.won + f.lost + f.still_open > 0) {
    out.push({
      key: "funnel",
      label: "SQO outcomes",
      description: "How this window's sales-qualified opportunities resolved.",
      kind: "category",
      chartTypes: ["bar", "horizontal-bar", "pie", "donut"],
      categoryKey: "stage",
      rows: [
        { stage: "Won", value: f.won },
        { stage: "Lost", value: f.lost },
        { stage: "Still open", value: f.still_open },
      ],
      series: [{ key: "value", name: "Deals" }],
      colors: [color("success"), color("danger"), color("warning")],
      valueFormat: count0,
    });
  }

  return out;
}
